
import {
  computed, defineComponent, onMounted, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  MEDIA_TYPES, NAVIGATION_PAGES, PHOTO_VIEW_TYPE, SHARE_ACCESS, SHARE_SETTINGS, SHARE_TYPES,
} from '@/types/enums';
import { ICase, IMedia } from '@/types/interfaces';
import { useMainStore } from '@/store/mainStore';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { lowResUrlByViewTypeByUrl, lowResUrlByViewType } from '@/helpers/lowResPhotoUrl';
import AppLazyImage from '@/components/UI/AppLazyImage.vue';
import MixedCaseSettingsPopUp from '@/components/BottomPopUp/PopUpTypes/Settings/MixedCaseSettingsPopUp.vue';
import DriveService from '@/modules/drive/services/DriveService';
import AppQuestionPopUp from '@/components/AppQuestionPopUp.vue';
import MoveToPopUp from '@/components/BottomPopUp/PopUpTypes/MoveTo/MoveToPopUp.vue';
import AppRenameCase from '@/components/AppRenameCase.vue';
import SharePopUp from '@/components/BottomPopUp/PopUpTypes/Share/SharePopUp.vue';
import { descriptionWithLink } from '@/helpers/utils';

export default defineComponent({
  props: {
    iCase: { type: Object as () => ICase, required: true },
    hideTitle: { type: Boolean, default: false },
    columnView: { type: Boolean, default: false },
    selector: { type: Boolean, default: false },
    isHmed: { type: Boolean, default: false },
  },
  components: {
    MoveToPopUp,
    AppQuestionPopUp,
    MixedCaseSettingsPopUp,
    AppLazyImage,
    AppRenameCase,
    SharePopUp,
  },
  emits: ['open-case'],
  setup(props, { emit }) {
    const mainStore = useMainStore();
    const driveStore = useDriveStore();
    const route = useRoute();
    const router = useRouter();
    const isCaseSettingsPopUp = ref(false);
    const isMoveToCasePopUp = ref(false);
    const isQuestionPopUp = ref(false);
    const isCaseCopying = ref(false);
    const caseRenamePopUp = ref(false);
    const isSharePopUp = ref(false);

    function getCasePhoto(file: IMedia | undefined) {
      if (file?.type === MEDIA_TYPES.VIDEO) {
        return {
          url: lowResUrlByViewType(file, PHOTO_VIEW_TYPE.THUMB),
          isVideo: true,
        };
      }
      if (file?.type === MEDIA_TYPES.PHOTO) {
        return {
          url: lowResUrlByViewType(file, props.isHmed ? PHOTO_VIEW_TYPE.WHMED : PHOTO_VIEW_TYPE.WHSML),
        };
      }
      if (file?.type === MEDIA_TYPES.FILES) {
        return {
          // eslint-disable-next-line global-require
          url: require('@/assets/img/graphic/file.svg'),
          class: 'photo-box-file',
        };
      }

      return undefined;
    }

    const caseMedia = computed(() => [...(props.iCase?.photos || []), ...(props.iCase?.files || [])]);

    const isNetwork = computed(() => route.meta.isNetwork);
    const myCase = computed(() => props.iCase.ownerId.internalId === mainStore.user.accountId
                                      || props.iCase.ownerId.internalId === mainStore.boundCorporate.internalId);
    const reshareAllowed = computed(() => props.iCase.shareSettings?.includes(SHARE_SETTINGS.RESHARE_ALLOWED));

    function openMovePopUp() {
      isCaseSettingsPopUp.value = false;
      isMoveToCasePopUp.value = true;
    }
    function openQuestionPopUp() {
      isCaseSettingsPopUp.value = false;
      isQuestionPopUp.value = true;
    }

    function openRenamePopUp() {
      caseRenamePopUp.value = true;
    }
    function goToCase(caseId: string) {
      router.push(`/drive/case/my/${caseId}/view/`).then(() => {
        driveStore.fullscreenFile = undefined;
        driveStore.fullscreenPhoto = undefined;
        mainStore.BottomPopUpType = null;
      });
    }

    function updateLocation(folderId: string | null) {
      driveStore.currentCase!.folderId = folderId;
    }
    function saveCaseAsCopy() {
      isCaseCopying.value = true;
      isCaseSettingsPopUp.value = false;
      driveStore.saveCaseAsCopy(props.iCase?.id as string).then((response) => {
        if (response.success) {
          isCaseCopying.value = false;
          console.log(response);
        }
      });
    }

    function removeCaseFromSharing() {
      const id = props.iCase?.id;
      if (id) {
        const selectedCases = {
          ids: [id as string],
          accessType: SHARE_ACCESS.SPECIFIC_PEOPLE,
          target: '',
          shareType: props.iCase.shareType,
        };
        driveStore.networkCases.data = driveStore.networkCases.data.filter((selectedCase) => selectedCase.id !== id);
        driveStore.networkMixedData.data = driveStore.networkMixedData.data.filter((selectedMediaItem) => selectedMediaItem.id !== id);
        DriveService.removeFromSharing(selectedCases).then(() => {
          driveStore.fetchCases().then(() => {
            isCaseSettingsPopUp.value = false;
          });
        });
      }
    }
    function deleteCase() {
      isQuestionPopUp.value = false;
      driveStore.deleteCase(props.iCase?.id as string);
      isCaseSettingsPopUp.value = false;
    }

    const photoUrl = computed(() => {
      if (props.iCase.ownerId.internalId !== mainStore.user.accountId) {
        if (props.iCase.owner?.photo?.url) {
          return lowResUrlByViewTypeByUrl(props.iCase.owner.photo.url, PHOTO_VIEW_TYPE.WHSML);
        }
        // eslint-disable-next-line global-require
        return require('@/assets/img/mockup/profile.svg');
      }
      return '';
    });
    const authorName = computed(() => `${props.iCase.owner.firstName} ${props.iCase.owner.lastName}`);

    function selectCase(iCase: ICase): void {
      if (!props.selector) {
        emit('open-case');
      }
      // eslint-disable-next-line no-param-reassign
      iCase.selected = !iCase.selected;
    }

    function onShare() {
      isSharePopUp.value = true;
      isCaseSettingsPopUp.value = false;
    }

    function onSharePopupHide() {
      isSharePopUp.value = false;
    }

    return {
      mainStore,
      caseMedia,
      photoUrl,
      authorName,
      isNetwork,
      myCase,
      reshareAllowed,
      getCasePhoto,
      selectCase,
      isCaseSettingsPopUp,
      openMovePopUp,
      saveCaseAsCopy,
      updateLocation,
      openQuestionPopUp,
      removeCaseFromSharing,
      isQuestionPopUp,
      deleteCase,
      isMoveToCasePopUp,
      SHARE_TYPES,
      caseRenamePopUp,
      openRenamePopUp,
      driveStore,
      descriptionWithLink,
      isSharePopUp,
      onShare,
      onSharePopupHide,
    };
  },
});
